import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { IMAGES } from 'config/images';
import { PATHS } from 'config/paths';
import { pressableSx } from 'utils/motion';
import { useGoBack } from 'utils/navigation';

const ellipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
} as const;

const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }
  return email ? email[0].toUpperCase() : '';
};

interface Props {
  /** 'brand' = zenjob wordmark (home); 'back' = back arrow + title (drill-ins). */
  variant?: 'brand' | 'back';
  title?: string;
  subtitle?: string;
  /** Show the avatar that opens the account sheet. Default true; pass false on
   *  account/settings pages, where re-opening the account sheet makes no sense. */
  showAvatar?: boolean;
}

/**
 * The single app header: a frosted, sticky, rounded-bottom bar. The left side is either
 * the zenjob wordmark (home) or a back arrow + screen title (drill-ins). The avatar on the
 * right opens the account sheet in the weeks flow; it is hidden on account/settings pages.
 */
export const AppTopBar = ({
  variant = 'brand',
  title,
  subtitle,
  showAvatar = true
}: Props) => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { user } = useAuth0();
  const initials = showAvatar ? getInitials(user?.name, user?.email) : '';

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        top: 0,
        bgcolor: 'rgba(244, 246, 248, 0.72)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: 'none',
        borderBottomLeftRadius: 20,
        borderBottomRightRadius: 20,
        pt: 'calc(env(safe-area-inset-top) + 16px)'
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: 56 }}>
        <Container
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          {variant === 'brand' ? (
            <Box
              component="img"
              src={IMAGES.proLogoPurple}
              alt="Zenjob PRO"
              sx={{ height: 22, width: 'auto' }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0
              }}
            >
              <IconButton
                onClick={goBack}
                aria-label="Go back"
                edge="start"
                sx={{ color: 'text.primary' }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                {title && (
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: 'text.primary', ...ellipsis }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'text.secondary',
                      ...ellipsis
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {showAvatar && (
            <ButtonBase
              onClick={() => navigate(PATHS.account)}
              data-testid="profile-avatar"
              aria-label="Open profile"
              sx={{
                ...pressableSx,
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'primary.lighter',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 15,
                flexShrink: 0
              }}
            >
              {initials}
            </ButtonBase>
          )}
        </Container>
      </Toolbar>
    </AppBar>
  );
};
